<script>
  import { apiRequest } from "./api/request.js";

  let activeTab = "plans"; // 'plans' | 'addons' | 'config'

  let globalError = null;
  let globalSuccess = null;

  // --- Plans state
  let plans = [];
  let plansLoading = false;
  let plansError = null;
  let includeDeletedPlans = false;
  let showInactivePlans = false; // default: active plans only

  // --- Addons state
  let addons = [];
  let addonsLoading = false;
  let addonsError = null;
  let includeDeletedAddons = false;
  let showInactiveAddons = false; // default: active addons only

  // Only show active items unless the user opts in to see inactive ones.
  $: visiblePlans = showInactivePlans ? plans : plans.filter((p) => p.is_active);
  $: visibleAddons = showInactiveAddons ? addons : addons.filter((a) => a.is_active);
  $: hiddenInactivePlans = plans.filter((p) => !p.is_active).length;
  $: hiddenInactiveAddons = addons.filter((a) => !a.is_active).length;

  // --- Config state
  let configLoading = false;
  let configError = null;
  let configSaving = false;
  let discountLabel = "";
  let featuresTable = [];

  // --- Plan modal state
  let showPlanModal = false;
  let planModalMode = "create"; // 'create' | 'edit'
  let planSubmitting = false;
  let planFormError = "";
  let planForm = emptyPlanForm();

  // --- Addon modal state
  let showAddonModal = false;
  let addonModalMode = "create"; // 'create' | 'edit'
  let addonSubmitting = false;
  let addonFormError = "";
  let addonForm = emptyAddonForm();

  const dismissGlobal = () => {
    globalError = null;
    globalSuccess = null;
  };

  const setSuccess = (msg) => {
    globalSuccess = msg;
    globalError = null;
    setTimeout(() => {
      if (globalSuccess === msg) globalSuccess = null;
    }, 3500);
  };

  const setError = (msg) => {
    globalError = msg;
    globalSuccess = null;
  };

  const clone = (v) => JSON.parse(JSON.stringify(v ?? null));

  function emptyPlanForm() {
    return {
      plan_id: "",
      chargebee_plan_id: "",
      billing: "yearly",
      plan_title: "",
      plan_discount_label: "",
      highlight: "",
      billing_cycle_label: "",
      default_storage_gb: 0,
      seat_min: 1,
      seat_max: 1,
      enabled_feature_list: [],
      is_active: true,
      sort_order: 0,
      features: [],
      full_features_rows: []
    };
  }

  function emptyAddonForm() {
    return {
      id: null,
      plan_id: "",
      chargebee_id: "",
      monthly_price: 0,
      yearly_price: 0,
      label: "",
      storage_bytes: 0,
      is_active: true,
      sort_order: 0
    };
  }

  const toNumber = (v, fallback = 0) => {
    if (v === "" || v === null || v === undefined) return fallback;
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  };

  const isHexColor = (v) => {
    if (!v) return true; // optional
    return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(v);
  };

  const fullFeaturesRowsFromObject = (obj) => {
    const o = obj && typeof obj === "object" ? obj : {};
    return Object.keys(o).map((k) => {
      const val = o[k];
      const isBool = typeof val === "boolean";
      return {
        key: k,
        type: isBool ? "boolean" : "string",
        bool_value: isBool ? val : false,
        string_value: isBool ? "" : String(val ?? "")
      };
    });
  };

  const fullFeaturesObjectFromRows = (rows) => {
    const out = {};
    for (const row of rows) {
      const k = (row.key || "").trim();
      if (!k) continue;
      out[k] = row.type === "boolean" ? Boolean(row.bool_value) : String(row.string_value ?? "");
    }
    return out;
  };

  // --- API loaders
  const loadPlans = async () => {
    plansLoading = true;
    plansError = null;
    try {
      const qs = includeDeletedPlans ? "?include_deleted=true" : "";
      const data = await apiRequest(`${process.env.API_ROOT}/admin/billing/v2/plan/list${qs}`);
      plans = data.plans || [];
    } catch (e) {
      plansError = e.message;
    } finally {
      plansLoading = false;
    }
  };

  const loadAddons = async () => {
    addonsLoading = true;
    addonsError = null;
    try {
      const qs = includeDeletedAddons ? "?include_deleted=true" : "";
      const data = await apiRequest(`${process.env.API_ROOT}/admin/billing/v2/storage-addon/list${qs}`);
      addons = data.addons || [];
    } catch (e) {
      addonsError = e.message;
    } finally {
      addonsLoading = false;
    }
  };

  const loadConfig = async () => {
    configLoading = true;
    configError = null;
    try {
      const data = await apiRequest(`${process.env.API_ROOT}/admin/billing/v2/config`);
      discountLabel = data.discountLabel || "";
      featuresTable = data.featuresTable || [];
    } catch (e) {
      configError = e.message;
    } finally {
      configLoading = false;
    }
  };

  const refreshActiveTab = async () => {
    dismissGlobal();
    if (activeTab === "plans") return await loadPlans();
    if (activeTab === "addons") return await loadAddons();
    if (activeTab === "config") return await loadConfig();
  };

  // lazy initial load
  const initialLoad = refreshActiveTab();

  $: if (activeTab === "plans") {
    // no-op: loaded via initialLoad, plus explicit refresh actions
  }
  $: if (activeTab === "addons") {
    // no-op
  }
  $: if (activeTab === "config") {
    // no-op
  }

  // --- Plans actions
  const openCreatePlanModal = () => {
    planModalMode = "create";
    planFormError = "";
    planForm = emptyPlanForm();
    showPlanModal = true;
  };

  const openEditPlanModal = (plan) => {
    planModalMode = "edit";
    planFormError = "";
    planForm = {
      plan_id: plan.plan_id || "",
      chargebee_plan_id: plan.chargebee_plan_id || "",
      billing: plan.billing || "yearly",
      plan_title: plan.plan_title || "",
      plan_discount_label: plan.plan_discount_label || "",
      highlight: plan.highlight || "",
      billing_cycle_label: plan.billing_cycle_label || "",
      default_storage_gb: plan.default_storage_gb ?? 0,
      seat_min: plan.seat_min ?? 1,
      seat_max: plan.seat_max ?? 1,
      enabled_feature_list: clone(plan.enabled_feature_list || []),
      is_active: Boolean(plan.is_active),
      sort_order: plan.sort_order ?? 0,
      features: clone(plan.features || []),
      full_features_rows: fullFeaturesRowsFromObject(plan.full_features || {})
    };
    showPlanModal = true;
  };

  const closePlanModal = () => {
    showPlanModal = false;
    planFormError = "";
    planForm = emptyPlanForm();
  };

  const addPlanFeature = () => {
    planForm = { ...planForm, features: [...(planForm.features || []), { icon: "", value: "" }] };
  };

  const removePlanFeature = (idx) => {
    const next = [...(planForm.features || [])];
    next.splice(idx, 1);
    planForm = { ...planForm, features: next };
  };

  const addFullFeatureRow = () => {
    planForm = {
      ...planForm,
      full_features_rows: [
        ...(planForm.full_features_rows || []),
        { key: "", type: "string", bool_value: false, string_value: "" }
      ]
    };
  };

  const removeFullFeatureRow = (idx) => {
    const next = [...(planForm.full_features_rows || [])];
    next.splice(idx, 1);
    planForm = { ...planForm, full_features_rows: next };
  };

  const addEnabledFlag = () => {
    planForm = {
      ...planForm,
      enabled_feature_list: [...(planForm.enabled_feature_list || []), ""]
    };
  };

  const removeEnabledFlag = (idx) => {
    const next = [...(planForm.enabled_feature_list || [])];
    next.splice(idx, 1);
    planForm = { ...planForm, enabled_feature_list: next };
  };

  const validatePlanForm = () => {
    if (planModalMode === "create") {
      if (!planForm.plan_id || !planForm.plan_id.trim()) return "plan_id is required";
      if (!planForm.billing) return "billing is required";
    }

    if (!isHexColor(planForm.highlight)) return "highlight must be a hex color like #2D83FF (or empty)";

    const seatMin = toNumber(planForm.seat_min, 0);
    const seatMax = toNumber(planForm.seat_max, 0);
    if (seatMin < 0 || seatMax < 0) return "seat_min and seat_max must be >= 0";
    if (seatMin > seatMax) return "seat_min cannot be greater than seat_max";

    const seen = new Set();
    for (const row of planForm.full_features_rows || []) {
      const k = (row.key || "").trim();
      if (!k) continue;
      if (seen.has(k)) return `Duplicate full_features key: "${k}"`;
      seen.add(k);
    }

    return null;
  };

  const submitPlan = async () => {
    planFormError = "";
    const err = validatePlanForm();
    if (err) {
      planFormError = err;
      return;
    }

    planSubmitting = true;
    try {
      const body = {
        plan_id: planForm.plan_id.trim(),
        billing: planForm.billing,
        chargebee_plan_id: planForm.chargebee_plan_id || planForm.plan_id.trim(),
        plan_title: planForm.plan_title,
        plan_discount_label: planForm.plan_discount_label,
        highlight: planForm.highlight,
        billing_cycle_label: planForm.billing_cycle_label,
        features: (planForm.features || [])
          .map((f) => ({
            value: String(f.value ?? "").trim(),
            icon: f.icon ? String(f.icon) : undefined
          }))
          .filter((f) => Boolean(f.value)),
        full_features: fullFeaturesObjectFromRows(planForm.full_features_rows || []),
        default_storage_gb: toNumber(planForm.default_storage_gb, 0),
        seat_min: toNumber(planForm.seat_min, 0),
        seat_max: toNumber(planForm.seat_max, 0),
        enabled_feature_list: (planForm.enabled_feature_list || []).map((x) => String(x ?? "").trim()).filter(Boolean),
        is_active: Boolean(planForm.is_active),
        sort_order: toNumber(planForm.sort_order, 0)
      };

      if (planModalMode === "create") {
        await apiRequest(`${process.env.API_ROOT}/admin/billing/v2/plan`, {
          method: "POST",
          body: JSON.stringify(body)
        });
        setSuccess("Billing plan created");
      } else {
        await apiRequest(`${process.env.API_ROOT}/admin/billing/v2/plan/${encodeURIComponent(planForm.plan_id)}`, {
          method: "PUT",
          body: JSON.stringify(body)
        });
        setSuccess("Billing plan updated");
      }

      closePlanModal();
      await loadPlans();
    } catch (e) {
      planFormError = e.message;
    } finally {
      planSubmitting = false;
    }
  };

  const togglePlanActive = async (plan) => {
    dismissGlobal();
    try {
      const action = plan.is_active ? "deactivate" : "activate";
      await apiRequest(`${process.env.API_ROOT}/admin/billing/v2/plan/${encodeURIComponent(plan.plan_id)}/${action}`, {
        method: "POST"
      });
      setSuccess(`Plan ${action}d`);
      await loadPlans();
    } catch (e) {
      setError(e.message);
    }
  };

  const deletePlan = async (plan) => {
    dismissGlobal();
    const ok = confirm(`Delete plan "${plan.plan_id}"?\n\nThis is a soft delete (sets deleted_at).`);
    if (!ok) return;
    try {
      await apiRequest(`${process.env.API_ROOT}/admin/billing/v2/plan/${encodeURIComponent(plan.plan_id)}`, {
        method: "DELETE"
      });
      setSuccess("Plan deleted");
      await loadPlans();
    } catch (e) {
      setError(e.message);
    }
  };

  // --- Addons actions
  const openCreateAddonModal = () => {
    addonModalMode = "create";
    addonFormError = "";
    addonForm = emptyAddonForm();
    showAddonModal = true;
  };

  const openEditAddonModal = (addon) => {
    addonModalMode = "edit";
    addonFormError = "";
    addonForm = {
      id: addon.id,
      plan_id: addon.plan_id || "",
      chargebee_id: addon.chargebee_id || "",
      monthly_price: addon.monthly_price ?? 0,
      yearly_price: addon.yearly_price ?? 0,
      label: addon.label || "",
      storage_bytes: addon.storage_bytes ?? 0,
      is_active: Boolean(addon.is_active),
      sort_order: addon.sort_order ?? 0
    };
    showAddonModal = true;
  };

  const closeAddonModal = () => {
    showAddonModal = false;
    addonFormError = "";
    addonForm = emptyAddonForm();
  };

  const validateAddonForm = () => {
    if (!addonForm.plan_id || !addonForm.plan_id.trim()) return "plan_id is required";
    if (!addonForm.chargebee_id || !addonForm.chargebee_id.trim()) return "chargebee_id is required";
    if (!addonForm.label || !addonForm.label.trim()) return "label is required";
    const bytes = toNumber(addonForm.storage_bytes, 0);
    if (bytes <= 0) return "storage_bytes must be > 0";
    return null;
  };

  const submitAddon = async () => {
    addonFormError = "";
    const err = validateAddonForm();
    if (err) {
      addonFormError = err;
      return;
    }

    addonSubmitting = true;
    try {
      const body = {
        plan_id: addonForm.plan_id.trim(),
        chargebee_id: addonForm.chargebee_id.trim(),
        monthly_price: toNumber(addonForm.monthly_price, 0),
        yearly_price: toNumber(addonForm.yearly_price, 0),
        label: addonForm.label,
        storage_bytes: toNumber(addonForm.storage_bytes, 0),
        is_active: Boolean(addonForm.is_active),
        sort_order: toNumber(addonForm.sort_order, 0)
      };

      if (addonModalMode === "create") {
        await apiRequest(`${process.env.API_ROOT}/admin/billing/v2/storage-addon`, {
          method: "POST",
          body: JSON.stringify(body)
        });
        setSuccess("Storage addon created");
      } else {
        await apiRequest(`${process.env.API_ROOT}/admin/billing/v2/storage-addon/${encodeURIComponent(addonForm.id)}`, {
          method: "PUT",
          body: JSON.stringify(body)
        });
        setSuccess("Storage addon updated");
      }

      closeAddonModal();
      await loadAddons();
    } catch (e) {
      addonFormError = e.message;
    } finally {
      addonSubmitting = false;
    }
  };

  const toggleAddonActive = async (addon) => {
    dismissGlobal();
    try {
      const action = addon.is_active ? "deactivate" : "activate";
      await apiRequest(`${process.env.API_ROOT}/admin/billing/v2/storage-addon/${encodeURIComponent(addon.id)}/${action}`, {
        method: "POST"
      });
      setSuccess(`Addon ${action}d`);
      await loadAddons();
    } catch (e) {
      setError(e.message);
    }
  };

  const deleteAddon = async (addon) => {
    dismissGlobal();
    const ok = confirm(`Delete storage addon #${addon.id} (${addon.label})?\n\nThis is a soft delete (sets deleted_at).`);
    if (!ok) return;
    try {
      await apiRequest(`${process.env.API_ROOT}/admin/billing/v2/storage-addon/${encodeURIComponent(addon.id)}`, {
        method: "DELETE"
      });
      setSuccess("Storage addon deleted");
      await loadAddons();
    } catch (e) {
      setError(e.message);
    }
  };

  // --- Config actions
  const addFeaturesSection = () => {
    featuresTable = [...(featuresTable || []), { title: "", children: [] }];
  };

  const removeFeaturesSection = (idx) => {
    const next = [...(featuresTable || [])];
    next.splice(idx, 1);
    featuresTable = next;
  };

  const addSectionChild = (sectionIdx) => {
    const next = clone(featuresTable || []);
    next[sectionIdx].children = [...(next[sectionIdx].children || []), { title: "", subtitle: "" }];
    featuresTable = next;
  };

  const removeSectionChild = (sectionIdx, childIdx) => {
    const next = clone(featuresTable || []);
    next[sectionIdx].children = [...(next[sectionIdx].children || [])];
    next[sectionIdx].children.splice(childIdx, 1);
    featuresTable = next;
  };

  const saveConfig = async () => {
    dismissGlobal();
    configSaving = true;
    try {
      for (const s of featuresTable || []) {
        const st = String(s.title ?? "").trim();
        if (!st) {
          throw new Error("featuresTable section title cannot be empty");
        }
        for (const c of s.children || []) {
          const ct = String(c.title ?? "").trim();
          if (!ct) {
            throw new Error("featuresTable row title cannot be empty");
          }
        }
      }

      const body = {
        discountLabel: discountLabel,
        featuresTable: (featuresTable || []).map((s) => ({
          title: String(s.title ?? ""),
          children: (s.children || []).map((c) => ({
            title: String(c.title ?? ""),
            subtitle: c.subtitle ? String(c.subtitle) : undefined
          }))
        }))
      };

      await apiRequest(`${process.env.API_ROOT}/admin/billing/v2/config`, {
        method: "PUT",
        body: JSON.stringify(body)
      });
      setSuccess("Billing config updated");
      await loadConfig();
    } catch (e) {
      setError(e.message);
    } finally {
      configSaving = false;
    }
  };
</script>

<div class="page-container">
  <div class="content-wrapper">
    <div class="ds-page-header">
      <div>
        <h1 class="ds-page-title">Billing Plans</h1>
        <p class="ds-page-subtitle">Edit what clients see on Billing V2 plan list (display-only)</p>
      </div>
      <div class="header-actions">
        <button class="ds-btn ds-btn-secondary" on:click={refreshActiveTab} disabled={plansLoading || addonsLoading || configLoading || configSaving}>
          Refresh
        </button>
      </div>
    </div>

    {#if globalError}
      <div class="ds-alert ds-alert-error">
        {globalError}
        <button class="dismiss-btn" on:click={dismissGlobal}>Dismiss</button>
      </div>
    {/if}

    {#if globalSuccess}
      <div class="ds-alert ds-alert-success">
        {globalSuccess}
        <button class="dismiss-btn" on:click={dismissGlobal}>Dismiss</button>
      </div>
    {/if}

    <div class="tabs">
      <button class="tab" class:active={activeTab === "plans"} on:click={() => { activeTab = "plans"; refreshActiveTab(); }}>
        Plans
      </button>
      <button class="tab" class:active={activeTab === "addons"} on:click={() => { activeTab = "addons"; refreshActiveTab(); }}>
        Storage Addons
      </button>
      <button class="tab" class:active={activeTab === "config"} on:click={() => { activeTab = "config"; refreshActiveTab(); }}>
        Config
      </button>
    </div>

    {#if activeTab === "plans"}
      <div class="toolbar">
        <label class="checkbox">
          <input type="checkbox" bind:checked={showInactivePlans} />
          Show inactive{#if !showInactivePlans && hiddenInactivePlans > 0}&nbsp;({hiddenInactivePlans}){/if}
        </label>
        <label class="checkbox">
          <input type="checkbox" bind:checked={includeDeletedPlans} on:change={loadPlans} />
          Include deleted
        </label>
        <div class="spacer"></div>
        <button class="ds-btn ds-btn-primary" on:click={openCreatePlanModal}>Create plan</button>
      </div>

      {#if plansLoading}
        <div class="loading-state">
          <div class="spinner"></div>
          <p>Loading plans…</p>
        </div>
      {:else if plansError}
        <div class="error-state">
          <p>{plansError}</p>
          <button class="ds-btn ds-btn-secondary ds-btn-sm" on:click={loadPlans}>Retry</button>
        </div>
      {:else}
        <div class="ds-card">
          <table class="ds-table">
            <thead>
              <tr>
                <th>Sort</th>
                <th>Plan ID</th>
                <th>Title</th>
                <th>Billing</th>
                <th>Seats</th>
                <th>Storage (GB)</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {#each visiblePlans as plan}
                <tr class:row-deleted={Boolean(plan.deleted_at)}>
                  <td class="mono">{plan.sort_order ?? "-"}</td>
                  <td class="mono">{plan.plan_id}</td>
                  <td>{plan.plan_title || "-"}</td>
                  <td class="mono">{plan.billing || "-"}</td>
                  <td class="mono">{plan.seat_min ?? "-"}–{plan.seat_max ?? "-"}</td>
                  <td class="mono">{plan.default_storage_gb ?? "-"}</td>
                  <td>
                    {#if plan.deleted_at}
                      <span class="ds-chip ds-chip-danger">deleted</span>
                    {:else if plan.is_active}
                      <span class="ds-chip ds-chip-ok">active</span>
                    {:else}
                      <span class="ds-chip ds-chip-warn">inactive</span>
                    {/if}
                  </td>
                  <td>
                    <div class="actions">
                      <button class="ds-btn ds-btn-secondary ds-btn-sm" on:click={() => openEditPlanModal(plan)}>Edit</button>
                      <button class="ds-btn ds-btn-secondary ds-btn-sm" on:click={() => togglePlanActive(plan)} disabled={Boolean(plan.deleted_at)}>
                        {plan.is_active ? "Deactivate" : "Activate"}
                      </button>
                      <button class="ds-btn ds-btn-danger ds-btn-sm" on:click={() => deletePlan(plan)} disabled={Boolean(plan.deleted_at)}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              {/each}
              {#if visiblePlans.length === 0}
                <tr><td colspan="8" class="empty-state">{plans.length === 0 ? "No plans found." : "No active plans. Toggle “Show inactive” to view inactive plans."}</td></tr>
              {/if}
            </tbody>
          </table>
        </div>
      {/if}
    {/if}

    {#if activeTab === "addons"}
      <div class="toolbar">
        <label class="checkbox">
          <input type="checkbox" bind:checked={showInactiveAddons} />
          Show inactive{#if !showInactiveAddons && hiddenInactiveAddons > 0}&nbsp;({hiddenInactiveAddons}){/if}
        </label>
        <label class="checkbox">
          <input type="checkbox" bind:checked={includeDeletedAddons} on:change={loadAddons} />
          Include deleted
        </label>
        <div class="spacer"></div>
        <button class="ds-btn ds-btn-primary" on:click={openCreateAddonModal}>Create addon</button>
      </div>

      {#if addonsLoading}
        <div class="loading-state">
          <div class="spinner"></div>
          <p>Loading storage addons…</p>
        </div>
      {:else if addonsError}
        <div class="error-state">
          <p>{addonsError}</p>
          <button class="ds-btn ds-btn-secondary ds-btn-sm" on:click={loadAddons}>Retry</button>
        </div>
      {:else}
        <div class="ds-card">
          <table class="ds-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Plan ID</th>
                <th>Label</th>
                <th>Storage bytes</th>
                <th>Monthly</th>
                <th>Yearly</th>
                <th>Sort</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {#each visibleAddons as addon}
                <tr class:row-deleted={Boolean(addon.deleted_at)}>
                  <td class="mono">{addon.id}</td>
                  <td class="mono">{addon.plan_id || "-"}</td>
                  <td>{addon.label || "-"}</td>
                  <td class="mono">{addon.storage_bytes ?? "-"}</td>
                  <td class="mono">{addon.monthly_price ?? "-"}</td>
                  <td class="mono">{addon.yearly_price ?? "-"}</td>
                  <td class="mono">{addon.sort_order ?? "-"}</td>
                  <td>
                    {#if addon.deleted_at}
                      <span class="ds-chip ds-chip-danger">deleted</span>
                    {:else if addon.is_active}
                      <span class="ds-chip ds-chip-ok">active</span>
                    {:else}
                      <span class="ds-chip ds-chip-warn">inactive</span>
                    {/if}
                  </td>
                  <td>
                    <div class="actions">
                      <button class="ds-btn ds-btn-secondary ds-btn-sm" on:click={() => openEditAddonModal(addon)}>Edit</button>
                      <button class="ds-btn ds-btn-secondary ds-btn-sm" on:click={() => toggleAddonActive(addon)} disabled={Boolean(addon.deleted_at)}>
                        {addon.is_active ? "Deactivate" : "Activate"}
                      </button>
                      <button class="ds-btn ds-btn-danger ds-btn-sm" on:click={() => deleteAddon(addon)} disabled={Boolean(addon.deleted_at)}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              {/each}
              {#if visibleAddons.length === 0}
                <tr><td colspan="9" class="empty-state">{addons.length === 0 ? "No storage addons found." : "No active addons. Toggle “Show inactive” to view inactive addons."}</td></tr>
              {/if}
            </tbody>
          </table>
        </div>
      {/if}
    {/if}

    {#if activeTab === "config"}
      {#if configLoading}
        <div class="loading-state">
          <div class="spinner"></div>
          <p>Loading config…</p>
        </div>
      {:else if configError}
        <div class="error-state">
          <p>{configError}</p>
          <button class="ds-btn ds-btn-secondary ds-btn-sm" on:click={loadConfig}>Retry</button>
        </div>
      {:else}
        <div class="ds-card">
          <h3>Global discount label</h3>
          <input class="ds-input" bind:value={discountLabel} placeholder="Save 20% with annual billing" />
        </div>

        <div class="ds-card">
          <div class="card-header-row">
            <h3>Features table</h3>
            <button class="ds-btn ds-btn-secondary ds-btn-sm" on:click={addFeaturesSection}>Add section</button>
          </div>

          {#if (featuresTable || []).length === 0}
            <div class="muted">No sections yet.</div>
          {/if}

          <div class="sections">
            {#each featuresTable as sectionItem, sectionIdx}
              <div class="section">
                <div class="section-header">
                  <div class="section-title">
                    <label class="small-label" for={"bpv2-config-section-title-" + sectionIdx}>Section title</label>
                    <input id={"bpv2-config-section-title-" + sectionIdx} class="ds-input" bind:value={sectionItem.title} placeholder="Users" />
                  </div>
                  <div class="section-actions">
                    <button class="ds-btn ds-btn-secondary ds-btn-sm" on:click={() => addSectionChild(sectionIdx)}>Add row</button>
                    <button class="ds-btn ds-btn-danger ds-btn-sm" on:click={() => removeFeaturesSection(sectionIdx)}>Remove section</button>
                  </div>
                </div>

                <div class="children">
                  {#each (sectionItem.children || []) as child, childIdx}
                    <div class="child-row">
                      <div class="child-field">
                        <label class="small-label" for={"bpv2-config-row-title-" + sectionIdx + "-" + childIdx}>Row title</label>
                        <input id={"bpv2-config-row-title-" + sectionIdx + "-" + childIdx} class="ds-input" bind:value={child.title} placeholder="Available seats" />
                      </div>
                      <div class="child-field">
                        <label class="small-label" for={"bpv2-config-row-subtitle-" + sectionIdx + "-" + childIdx}>Subtitle (optional)</label>
                        <input id={"bpv2-config-row-subtitle-" + sectionIdx + "-" + childIdx} class="ds-input" bind:value={child.subtitle} placeholder="Invite members to join your workspace" />
                      </div>
                      <div class="child-actions">
                        <button class="ds-btn ds-btn-danger ds-btn-sm" on:click={() => removeSectionChild(sectionIdx, childIdx)}>Remove</button>
                      </div>
                    </div>
                  {/each}
                  {#if (sectionItem.children || []).length === 0}
                    <div class="muted">No rows yet.</div>
                  {/if}
                </div>
              </div>
            {/each}
          </div>
        </div>

        <div class="footer-actions">
          <button class="ds-btn ds-btn-primary" on:click={saveConfig} disabled={configSaving}>
            {#if configSaving}Saving…{:else}Save config{/if}
          </button>
        </div>
      {/if}
    {/if}
  </div>
</div>

<!-- Plan Create/Edit Modal -->
{#if showPlanModal}
  <div class="modal-overlay" on:click={closePlanModal}>
    <div class="billing-modal" on:click|stopPropagation>
      <button class="modal-close" on:click={closePlanModal}>&times;</button>
      <h2>{planModalMode === "create" ? "Create Billing Plan" : "Edit Billing Plan"}</h2>

      <div class="billing-modal-content">
        <div class="form-grid">
        <div class="ds-field">
          <label class="ds-label" for="bpv2-plan-id">Plan ID</label>
          <input
            id="bpv2-plan-id"
            class="ds-input"
            bind:value={planForm.plan_id}
            placeholder="workspace-team-seats-yearly"
            disabled={planSubmitting || planModalMode === "edit"}
          />
        </div>

        <div class="ds-field">
          <label class="ds-label" for="bpv2-plan-billing">Billing</label>
          <select id="bpv2-plan-billing" class="ds-select" bind:value={planForm.billing} disabled={planSubmitting}>
            <option value="yearly">yearly</option>
            <option value="monthly">monthly</option>
          </select>
        </div>

        <div class="ds-field">
          <label class="ds-label" for="bpv2-plan-chargebee-id">Chargebee plan id</label>
          <input id="bpv2-plan-chargebee-id" class="ds-input" bind:value={planForm.chargebee_plan_id} placeholder="workspace-team-seats-yearly" disabled={planSubmitting} />
        </div>

        <div class="ds-field">
          <label class="ds-label" for="bpv2-plan-title">Title</label>
          <input id="bpv2-plan-title" class="ds-input" bind:value={planForm.plan_title} placeholder="Professional" disabled={planSubmitting} />
        </div>

        <div class="ds-field">
          <label class="ds-label" for="bpv2-plan-discount-label">Discount label</label>
          <input id="bpv2-plan-discount-label" class="ds-input" bind:value={planForm.plan_discount_label} placeholder="Save with annual billing" disabled={planSubmitting} />
        </div>

        <div class="ds-field">
          <label class="ds-label" for="bpv2-plan-billing-cycle-label">Billing cycle label</label>
          <input id="bpv2-plan-billing-cycle-label" class="ds-input" bind:value={planForm.billing_cycle_label} placeholder="Billed yearly" disabled={planSubmitting} />
        </div>

        <div class="ds-field">
          <label class="ds-label" for="bpv2-plan-highlight">Highlight color (hex)</label>
          <input id="bpv2-plan-highlight" class="ds-input" bind:value={planForm.highlight} placeholder="#2D83FF" disabled={planSubmitting} />
        </div>

        <div class="ds-field">
          <label class="ds-label" for="bpv2-plan-sort-order">Sort order</label>
          <input id="bpv2-plan-sort-order" type="number" class="ds-input" bind:value={planForm.sort_order} min="0" disabled={planSubmitting} />
        </div>

        <div class="ds-field">
          <label class="ds-label" for="bpv2-plan-seat-min">Seat min</label>
          <input id="bpv2-plan-seat-min" type="number" class="ds-input" bind:value={planForm.seat_min} min="0" disabled={planSubmitting} />
        </div>

        <div class="ds-field">
          <label class="ds-label" for="bpv2-plan-seat-max">Seat max</label>
          <input id="bpv2-plan-seat-max" type="number" class="ds-input" bind:value={planForm.seat_max} min="0" disabled={planSubmitting} />
        </div>

        <div class="ds-field">
          <label class="ds-label" for="bpv2-plan-default-storage">Default storage (GB)</label>
          <input id="bpv2-plan-default-storage" type="number" class="ds-input" bind:value={planForm.default_storage_gb} min="0" disabled={planSubmitting} />
        </div>

        <div class="ds-field checkbox-row">
          <label class="checkbox">
            <input type="checkbox" bind:checked={planForm.is_active} disabled={planSubmitting} />
            Active
          </label>
        </div>
      </div>

      <div class="editor-block">
        <div class="editor-header">
          <h3>Plan card bullets (features)</h3>
          <button class="ds-btn ds-btn-secondary ds-btn-sm" on:click={addPlanFeature} disabled={planSubmitting}>Add bullet</button>
        </div>
        {#each (planForm.features || []) as feature, idx}
          <div class="row-grid">
            <div class="ds-field">
              <label class="ds-label" for={"bpv2-plan-feature-icon-" + idx}>Icon</label>
              <select id={"bpv2-plan-feature-icon-" + idx} class="ds-select" bind:value={planForm.features[idx].icon} disabled={planSubmitting}>
                <option value="">(none)</option>
                <option value="filled">filled</option>
                <option value="line">line</option>
              </select>
            </div>
            <div class="ds-field">
              <label class="ds-label" for={"bpv2-plan-feature-value-" + idx}>Value</label>
              <input id={"bpv2-plan-feature-value-" + idx} class="ds-input" bind:value={planForm.features[idx].value} placeholder="1–3 seats" disabled={planSubmitting} />
            </div>
            <div class="row-actions">
              <button class="ds-btn ds-btn-danger ds-btn-sm" on:click={() => removePlanFeature(idx)} disabled={planSubmitting}>Remove</button>
            </div>
          </div>
        {/each}
        {#if (planForm.features || []).length === 0}
          <div class="muted">No bullets yet.</div>
        {/if}
      </div>

      <div class="editor-block">
        <div class="editor-header">
          <h3>Comparison table (full_features)</h3>
          <button class="ds-btn ds-btn-secondary ds-btn-sm" on:click={addFullFeatureRow} disabled={planSubmitting}>Add row</button>
        </div>
        {#each (planForm.full_features_rows || []) as row, idx}
          <div class="row-grid">
            <div class="ds-field">
              <label class="ds-label" for={"bpv2-plan-full-key-" + idx}>Key</label>
              <input id={"bpv2-plan-full-key-" + idx} class="ds-input" bind:value={planForm.full_features_rows[idx].key} placeholder="Offline playback" disabled={planSubmitting} />
            </div>
            <div class="ds-field">
              <label class="ds-label" for={"bpv2-plan-full-type-" + idx}>Type</label>
              <select id={"bpv2-plan-full-type-" + idx} class="ds-select" bind:value={planForm.full_features_rows[idx].type} disabled={planSubmitting}>
                <option value="string">string</option>
                <option value="boolean">boolean</option>
              </select>
            </div>
            <div class="ds-field">
              <label class="ds-label" for={"bpv2-plan-full-value-" + idx}>Value</label>
              {#if row.type === "boolean"}
                <label class="checkbox">
                  <input type="checkbox" bind:checked={planForm.full_features_rows[idx].bool_value} disabled={planSubmitting} />
                  true
                </label>
              {:else}
                <input id={"bpv2-plan-full-value-" + idx} class="ds-input" bind:value={planForm.full_features_rows[idx].string_value} placeholder="Unlimited" disabled={planSubmitting} />
              {/if}
            </div>
            <div class="row-actions">
              <button class="ds-btn ds-btn-danger ds-btn-sm" on:click={() => removeFullFeatureRow(idx)} disabled={planSubmitting}>Remove</button>
            </div>
          </div>
        {/each}
        {#if (planForm.full_features_rows || []).length === 0}
          <div class="muted">No comparison rows yet.</div>
        {/if}
      </div>

      <div class="editor-block">
        <div class="editor-header">
          <h3>Enabled feature flags (enabled_feature_list)</h3>
          <button class="ds-btn ds-btn-secondary ds-btn-sm" on:click={addEnabledFlag} disabled={planSubmitting}>Add flag</button>
        </div>
        {#each (planForm.enabled_feature_list || []) as flag, idx}
          <div class="row-grid">
            <div class="ds-field">
              <label class="ds-label" for={"bpv2-plan-flag-" + idx}>Flag</label>
              <input id={"bpv2-plan-flag-" + idx} class="ds-input" bind:value={planForm.enabled_feature_list[idx]} placeholder="SHARE_LINK" disabled={planSubmitting} />
            </div>
            <div class="row-actions">
              <button class="ds-btn ds-btn-danger ds-btn-sm" on:click={() => removeEnabledFlag(idx)} disabled={planSubmitting}>Remove</button>
            </div>
          </div>
        {/each}
        {#if (planForm.enabled_feature_list || []).length === 0}
          <div class="muted">No flags yet.</div>
        {/if}
      </div>

      {#if planFormError}
        <div class="form-error">{planFormError}</div>
      {/if}
      </div>

      <div class="form-actions">
        <button class="ds-btn ds-btn-secondary" on:click={closePlanModal} disabled={planSubmitting}>Cancel</button>
        <button class="ds-btn ds-btn-primary" on:click={submitPlan} disabled={planSubmitting}>
          {#if planSubmitting}
            <span class="btn-spinner"></span>
            Saving…
          {:else}
            Save
          {/if}
        </button>
      </div>
    </div>
  </div>
{/if}

<!-- Addon Create/Edit Modal -->
{#if showAddonModal}
  <div class="modal-overlay" on:click={closeAddonModal}>
    <div class="billing-modal" on:click|stopPropagation>
      <button class="modal-close" on:click={closeAddonModal}>&times;</button>
      <h2>{addonModalMode === "create" ? "Create Storage Addon" : "Edit Storage Addon"}</h2>

      <div class="billing-modal-content">
        <div class="form-grid">
        {#if addonModalMode === "edit"}
          <div class="ds-field">
            <label class="ds-label" for="bpv2-addon-id">ID</label>
            <input id="bpv2-addon-id" class="mono ds-input" value={addonForm.id} disabled />
          </div>
        {/if}

        <div class="ds-field">
          <label class="ds-label" for="bpv2-addon-plan-id">Plan ID</label>
          <input id="bpv2-addon-plan-id" class="ds-input" bind:value={addonForm.plan_id} placeholder="workspace-team-seats-yearly" disabled={addonSubmitting} />
        </div>

        <div class="ds-field">
          <label class="ds-label" for="bpv2-addon-chargebee-id">Chargebee addon id</label>
          <input id="bpv2-addon-chargebee-id" class="ds-input" bind:value={addonForm.chargebee_id} placeholder="1-tb-storage-yearly" disabled={addonSubmitting} />
        </div>

        <div class="ds-field">
          <label class="ds-label" for="bpv2-addon-label">Label</label>
          <input id="bpv2-addon-label" class="ds-input" bind:value={addonForm.label} placeholder="1 TB" disabled={addonSubmitting} />
        </div>

        <div class="ds-field">
          <label class="ds-label" for="bpv2-addon-storage-bytes">Storage bytes</label>
          <input id="bpv2-addon-storage-bytes" type="number" class="ds-input" bind:value={addonForm.storage_bytes} min="0" disabled={addonSubmitting} />
        </div>

        <div class="ds-field">
          <label class="ds-label" for="bpv2-addon-monthly-price">Monthly price</label>
          <input id="bpv2-addon-monthly-price" type="number" class="ds-input" bind:value={addonForm.monthly_price} min="0" disabled={addonSubmitting} />
        </div>

        <div class="ds-field">
          <label class="ds-label" for="bpv2-addon-yearly-price">Yearly price</label>
          <input id="bpv2-addon-yearly-price" type="number" class="ds-input" bind:value={addonForm.yearly_price} min="0" disabled={addonSubmitting} />
        </div>

        <div class="ds-field">
          <label class="ds-label" for="bpv2-addon-sort-order">Sort order</label>
          <input id="bpv2-addon-sort-order" type="number" class="ds-input" bind:value={addonForm.sort_order} min="0" disabled={addonSubmitting} />
        </div>

        <div class="ds-field checkbox-row">
          <label class="checkbox">
            <input type="checkbox" bind:checked={addonForm.is_active} disabled={addonSubmitting} />
            Active
          </label>
        </div>
      </div>

      {#if addonFormError}
        <div class="form-error">{addonFormError}</div>
      {/if}
      </div>

      <div class="form-actions">
        <button class="ds-btn ds-btn-secondary" on:click={closeAddonModal} disabled={addonSubmitting}>Cancel</button>
        <button class="ds-btn ds-btn-primary" on:click={submitAddon} disabled={addonSubmitting}>
          {#if addonSubmitting}
            <span class="btn-spinner"></span>
            Saving…
          {:else}
            Save
          {/if}
        </button>
      </div>
    </div>
  </div>
{/if}

<style>
  .page-container {
    padding: 2rem;
    max-width: 1600px;
    margin: 0 auto;
  }

  .content-wrapper {
    position: relative;
  }

  .header-actions {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .tabs {
    display: flex;
    gap: 0.25rem;
    border-bottom: 1px solid var(--ds-border-3);
    margin-bottom: 1.75rem;
    overflow-x: auto;
  }

  .tab {
    appearance: none;
    border: none;
    background: transparent;
    padding: 0.7rem 1rem;
    cursor: pointer;
    color: var(--ds-text-muted);
    font-weight: 600;
    font-size: 0.95rem;
    margin: 0;
    border-bottom: 2px solid transparent;
    transition: color 0.15s, border-color 0.15s;
  }

  .tab:hover {
    color: var(--ds-text);
  }

  .tab.active {
    color: var(--ds-accent);
    border-bottom-color: var(--ds-accent);
  }

  .toolbar {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1.5rem;
    padding: 1rem;
    background: var(--ds-surface);
    border-radius: var(--ds-radius-lg);
    box-shadow: var(--ds-shadow-card);
    border: 1px solid var(--ds-border);
  }

  .spacer {
    flex: 1;
  }

  .checkbox {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    color: #374151;
    font-size: 0.875rem;
    cursor: pointer;
  }

  .checkbox input[type="checkbox"] {
    cursor: pointer;
  }

  .dismiss-btn {
    background: none;
    border: none;
    font-weight: 600;
    cursor: pointer;
    opacity: 0.8;
    color: inherit;
    margin: 0;
  }

  .dismiss-btn:hover {
    opacity: 1;
  }

  .row-deleted {
    opacity: 0.6;
    background-color: var(--ds-danger-soft);
  }

  .row-deleted:hover {
    background-color: #fee2e2;
  }

  .mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  }

  .actions {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

  .loading-state, .error-state, .empty-state {
    padding: 3rem;
    text-align: center;
    color: var(--ds-text-muted);
  }

  .error-state button {
    margin-top: 1rem;
  }

  .spinner {
    border: 3px solid #f3f3f3;
    border-top: 3px solid var(--ds-accent);
    border-radius: 50%;
    width: 24px;
    height: 24px;
    animation: spin 1s linear infinite;
    margin: 0 auto 1rem auto;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .ds-card h3 {
    margin: 0 0 1rem 0;
    font-size: 1.125rem;
    font-weight: 600;
    color: var(--ds-text);
  }

  .card-header-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 1rem;
  }

  .sections {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .section {
    border: 1px solid var(--ds-border-3);
    border-radius: var(--ds-radius-md);
    padding: 1rem;
    background: var(--ds-surface-2);
  }

  .section-header {
    display: flex;
    gap: 1rem;
    justify-content: space-between;
    align-items: flex-end;
    flex-wrap: wrap;
    margin-bottom: 0.75rem;
  }

  .section-actions {
    display: flex;
    gap: 0.5rem;
    align-items: center;
  }

  .children {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .child-row {
    display: grid;
    grid-template-columns: 1fr 1fr auto;
    gap: 0.75rem;
    align-items: end;
  }

  .child-actions {
    display: flex;
    justify-content: flex-end;
  }

  .muted {
    color: var(--ds-text-muted);
    font-size: 0.875rem;
  }

  .footer-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 1rem;
  }

  /* Modal customizations */
  .modal-overlay {
    align-items: flex-start;
    padding: 2rem 1rem;
    overflow-y: auto;
  }

  .billing-modal {
    background: var(--ds-surface);
    border-radius: var(--ds-radius-md);
    padding: 0;
    width: 100%;
    max-width: 1000px;
    max-height: calc(100vh - 4rem);
    overflow: hidden;
    position: relative;
    box-shadow: var(--ds-shadow-modal);
    display: flex;
    flex-direction: column;
  }

  .billing-modal h2 {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 600;
    color: var(--ds-text);
    padding: 1.5rem 1.5rem 1rem;
    border-bottom: 1px solid var(--ds-border-3);
  }

  .billing-modal .modal-close {
    position: absolute;
    top: 1.25rem;
    right: 1.25rem;
    background: none;
    border: none;
    font-size: 1.75rem;
    cursor: pointer;
    color: var(--ds-text-muted);
    line-height: 1;
    padding: 0.25rem;
    width: 2rem;
    height: 2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--ds-radius-sm);
    transition: all 0.15s ease;
  }

  .billing-modal .modal-close:hover {
    background: var(--ds-surface-3);
    color: #374151;
  }

  .billing-modal-content {
    padding: 1.5rem;
    overflow-y: auto;
    flex: 1;
  }

  .form-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 1.25rem;
    margin-bottom: 1.5rem;
  }

  .billing-modal .ds-field {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
  }

  .billing-modal .ds-input:disabled,
  .billing-modal .ds-select:disabled {
    background: var(--ds-surface-3);
    color: var(--ds-text-muted);
    cursor: not-allowed;
  }

  .billing-modal .ds-input::placeholder {
    color: var(--ds-text-subtle);
  }

  .editor-block {
    border-top: 1px solid var(--ds-border-3);
    padding-top: 1.5rem;
    margin-top: 1.5rem;
  }

  .editor-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 1rem;
  }

  .editor-block h3 {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
    color: var(--ds-text);
  }

  .row-grid {
    display: grid;
    grid-template-columns: 200px 1fr auto;
    gap: 1rem;
    align-items: end;
    margin-bottom: 0.75rem;
    padding: 0.75rem;
    background: var(--ds-surface-2);
    border-radius: var(--ds-radius-md);
    border: 1px solid var(--ds-border-3);
  }

  .row-grid .ds-field {
    margin: 0;
  }

  .row-actions {
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }

  .small-label {
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--ds-text-muted);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 0.375rem;
  }

  .checkbox-row {
    display: flex;
    align-items: center;
    padding-top: 1.75rem;
  }

  .billing-modal .checkbox {
    font-size: 0.875rem;
    color: #374151;
  }

  .billing-modal .checkbox input[type="checkbox"] {
    width: 1rem;
    height: 1rem;
    cursor: pointer;
  }

  .form-error {
    background: var(--ds-danger-soft);
    border: 1px solid var(--ds-danger-border);
    color: var(--ds-danger);
    padding: 0.875rem 1rem;
    border-radius: var(--ds-radius-md);
    font-size: 0.875rem;
    margin-bottom: 1rem;
  }

  .form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    padding: 1.5rem;
    border-top: 1px solid var(--ds-border-3);
    background: var(--ds-surface-2);
  }

  .btn-spinner {
    display: inline-block;
    width: 0.875rem;
    height: 0.875rem;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-radius: 50%;
    border-top-color: white;
    animation: spin 0.8s linear infinite;
    margin-right: 0.5rem;
    vertical-align: middle;
  }

  .muted {
    color: var(--ds-text-subtle);
    font-size: 0.875rem;
    font-style: italic;
    padding: 0.5rem 0;
  }

  @media (max-width: 1024px) {
    .form-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
    .row-grid {
      grid-template-columns: 180px 1fr auto;
    }
  }

  @media (max-width: 768px) {
    .page-container { padding: 1rem; }
    .form-grid { grid-template-columns: 1fr; }
    .row-grid { grid-template-columns: 1fr; }
    .child-row { grid-template-columns: 1fr; }
    .billing-modal { max-width: 95vw; }
    .editor-header {
      flex-direction: column;
      align-items: flex-start;
      gap: 0.75rem;
    }
  }
</style>
